import type { PortalSalesRepInfoDto } from '@supply-mover-2-0/shared';
import { useQuery } from '@tanstack/react-query';
import * as portalAccountService from '../services/portalAccountService';
import { usePortalAuth } from './usePortalAuth';

/**
 * The active account's assigned salesperson (PD-15235), shared by every
 * surface that shows it (Get Help widget, header support slot) so the
 * lookup runs once per active client, not once per surface.
 *
 * Null while signed out, while loading, when the portal site opts out
 * (settings.showSalesRepInfo), or when the account has no assigned rep —
 * callers hide the block on null in every case. Re-fetches when the
 * active client changes (multi-account users switching locations).
 */
export function useSalesRepInfo(enabled = true): PortalSalesRepInfoDto | null {
	const { token, activeClientId } = usePortalAuth();

	const query = useQuery({
		queryKey: ['portal', 'sales-rep', activeClientId],
		queryFn: () => portalAccountService.getSalesRepInfo(token ?? ''),
		enabled: enabled && token != null && activeClientId != null,
		staleTime: 60_000,
	});

	return query.data ?? null;
}
